import { type DraftTarget, type ReportDesignDraft } from '@lib/customization/reportDesign';
import { useLocalization } from '@lib/localization';
import { Box, Stack, Typography } from '@mui/material';
import {
  flowClipStyles, noResultsStyles, sheetGroupStyles, sheetHeaderStyles, sheetStyles, sheetTitleStyles, stripPlaceStyles,
} from './DesignCanvas.styles';
import { A4, type Print } from './print';
import { flowPageStyles, unprintedStyles } from './print.styles';
import Region from './Region';
import { CheckpointsView, ChromeStrip, SummaryView, type PageNumber } from './ReportParts';
import { usePointScale } from './usePointScale';

interface Props {
  draft: ReportDesignDraft;
  /** Where the first page of the results stands in the report. Left out while the report has no such page. */
  page?: PageNumber;
  print: Print;
  isSelected: (target: DraftTarget) => boolean;
  isFlagged: (target: DraftTarget) => boolean;
  /** What is to be scrolled into view, when it is on this sheet (`useReveal`). */
  shown: (target: DraftTarget) => number | undefined;
  onSelect: (target: DraftTarget) => void;
}

/**
 * The first page of the results, between the covers and the closing pages: the header and
 * the footer strip, the summary in the template chosen for it and the first of the
 * checkpoints in theirs, drawn over the sample inspection.
 *
 * Each of the four is a button to its settings and nothing more: these are templates, set up
 * in the inspector, and nothing on this sheet is dragged. A section that is not printed
 * keeps a quiet line in its place, or there would be nothing to select it by; a strip that
 * is switched off draws nothing, as on the report, and is reached from the sections list.
 *
 * A design that prints neither section has no page of results, and `page` is left out
 * (`hasResultsPage`). The two lines then stand in a dashed place on the desk, with no paper
 * under them and no strips: a sheet there, with a header and a page number on it, would show
 * a page the report does not have.
 */
const ResultsSheet: React.FC<Props> = ({ draft, page, print, isSelected, isFlagged, shown, onSelect }) => {
  const { t } = useLocalization();
  const scale = usePointScale(A4.width);

  /** What a part's button takes, whichever part it is. */
  const part = (target: DraftTarget) => ({
    selected: isSelected(target),
    flagged: isFlagged(target),
    shown: shown(target),
    onSelect: () => onSelect(target),
  });

  // On paper the line is sized as print is. Off it, it is the desk's own text.
  const unprinted = (label: string) => (
    <Box sx={page ? unprintedStyles : undefined}>{`${label}: ${t('ReportDesign_Template_None')}`}</Box>
  );

  const sections = (
    <>
      <Region {...part({ kind: 'summary' })}>
        {draft.summary.template === 'none'
          ? unprinted(t('Common_Summary'))
          : <SummaryView segment={draft.summary} print={print} />}
      </Region>
      <Region {...part({ kind: 'checkpoints' })}>
        {draft.checkpoints.template === 'none'
          ? unprinted(t('Common_Checkpoints'))
          : <CheckpointsView segment={draft.checkpoints} print={print} />}
      </Region>
    </>
  );

  return (
    <Box sx={sheetGroupStyles}>
      <Stack direction="row" sx={sheetHeaderStyles}>
        <Typography sx={sheetTitleStyles}>{t('ReportDesign_Section_Results')}</Typography>
      </Stack>

      {/* One box with a page and without one: the scale is measured on the box it was first given (`usePointScale`). */}
      <Box ref={scale.ref} style={scale.style} sx={page ? sheetStyles : noResultsStyles}>
        {page ? (
          <Box sx={flowPageStyles}>
            <Box sx={flowClipStyles}>{sections}</Box>

            {(['header', 'footer'] as const).map((edge) => draft[edge].enabled && (
              <Region key={edge} {...part({ kind: edge })} sx={stripPlaceStyles[edge]}>
                <ChromeStrip chrome={draft[edge]} edge={edge} print={print} page={page} />
              </Region>
            ))}
          </Box>
        ) : sections}
      </Box>
    </Box>
  );
};

export default ResultsSheet;
